import { findConsecutiveRepeatedCharacter, findRepeatedWord } from './descriptionValidation';
import type { PaymentGatewayStatus } from '../services/platform/masterService';

export interface PaymentMethodExistingRecord {
  _id?: string;
  code?: string;
  name?: string;
  sortOrder?: number;
  isActive?: boolean;
  metadata?: Record<string, unknown>;
  gatewayStatus?: { value: string; label: string; kind: 'online' | 'offline'; ready: boolean; reason: string | null };
  toggleLock?: string | null;
  deleteLock?: string | null;
}

export interface PaymentMethodFormInput {
  code: string;
  name: string;
  description?: string;
  sortOrder: number;
  metadata: Record<string, unknown>;
}

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  name: 'Name',
  code: 'Code',
  sortOrder: 'Sort order',
  description: 'Description',
  gateway: 'Gateway',
  feePercent: 'Fee %',
  feeBasis: 'Fee basis',
};

export const PAYMENT_FEE_BASIS_LABELS: Record<string, string> = {
  none: 'No fee',
  platform: 'Platform fee',
  gateway: 'Gateway fee',
  both: 'Platform + gateway fee',
};

const NAME_PATTERN = /^\p{L}[\p{L}\s'-]*$/u;
const str = (v: unknown) => (v == null ? '' : String(v).trim());

export const normalizePaymentMethodName = (s: unknown) => String(s ?? '').replace(/\s+/g, ' ').trim();

export function paymentMethodCodeFromName(name: string): string {
  return name.toLowerCase().replace(/%/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
}

/** Mirrors the server: cash → Cash, wallet → Wallet, bank transfer → Bank transfer, everything else online. */
export function allowedGatewaysForMethod(code: string, gateways: PaymentGatewayStatus[]): PaymentGatewayStatus[] {
  const c = code.toLowerCase();
  if (/(^|_)cash(_|$)/.test(c)) return gateways.filter((g) => g.value === 'cash');
  if (/wallet/.test(c)) return gateways.filter((g) => g.value === 'wallet');
  if (/bank_transfer|neft|rtgs|imps/.test(c)) return gateways.filter((g) => g.value === 'bank_transfer');
  return gateways.filter((g) => g.kind === 'online');
}

export function paymentMethodOrderOf(row: PaymentMethodExistingRecord): number {
  const top = Number(row.sortOrder);
  if (Number.isInteger(top) && top > 0) return top;
  const meta = Number(row.metadata?.sortOrder);
  return Number.isInteger(meta) && meta > 0 ? meta : 0;
}

export function nextPaymentMethodSortOrder(existing: PaymentMethodExistingRecord[], editingId?: string) {
  return Math.max(0, ...existing.filter((r) => r._id !== editingId).map(paymentMethodOrderOf)) + 1;
}

/** Field errors keyed by field name; mirrors the server rules. */
export function validatePaymentMethodForm(
  form: PaymentMethodFormInput,
  ctx: { existing: PaymentMethodExistingRecord[]; editingId?: string; gateways: PaymentGatewayStatus[]; maxFeePercent: number },
): Record<string, string> {
  const errors: Record<string, string> = {};
  const others = ctx.existing.filter((r) => r._id !== ctx.editingId);
  const name = normalizePaymentMethodName(form.name);
  const originalName = ctx.editingId ? ctx.existing.find((r) => r._id === ctx.editingId)?.name : undefined;

  const nameChanged = name !== originalName;
  if (!name) errors.name = 'Name is required';
  else if (nameChanged && (name.length < 2 || name.length > 50)) errors.name = 'Name must be 2–50 characters';
  else if (nameChanged && !NAME_PATTERN.test(name)) errors.name = 'Letters, spaces, apostrophes and hyphens only — no numbers or special characters';

  const code = str(form.code);
  if (!code) {
    if (name && !errors.name) errors.code = 'Code could not be generated from the name';
  } else {
    const clash = others.find((r) => str(r.code).toLowerCase() === code.toLowerCase());
    if (clash) errors.code = `Code "${code}" is already used by "${clash.name}"`;
  }

  if (!Number.isInteger(form.sortOrder) || form.sortOrder < 1) {
    errors.sortOrder = 'Enter a whole number of 1 or more';
  } else {
    const clash = others.find((r) => paymentMethodOrderOf(r) === form.sortOrder);
    if (clash) errors.sortOrder = `Sort order ${form.sortOrder} is already used by "${clash.name}" — next available is ${nextPaymentMethodSortOrder(ctx.existing, ctx.editingId)}`;
  }

  const description = str(form.description);
  if (description.length > 500) {
    errors.description = 'Description must be 500 characters or fewer';
  } else if (description) {
    const run = findConsecutiveRepeatedCharacter(description);
    const word = findRepeatedWord(description);
    if (run) errors.description = `No repeated characters ("${run.slice(0, 4)}")`;
    else if (word) errors.description = `No repeated words ("${word}")`;
  }

  const gateway = str(form.metadata.gateway);
  const allowed = allowedGatewaysForMethod(code, ctx.gateways);
  const gatewayDef = ctx.gateways.find((g) => g.value === gateway);
  if (!gateway) {
    errors.gateway = 'Choose the payment gateway that processes this method';
  } else if (ctx.gateways.length && !allowed.some((g) => g.value === gateway)) {
    errors.gateway = allowed.length === 1
      ? `This method must use the ${allowed[0].label} gateway`
      : 'Online methods (card, UPI, net banking…) need an online gateway';
  }

  const rawFee = form.metadata.feePercent;
  const fee = rawFee === undefined || rawFee === null || str(rawFee) === '' ? 0 : Number(rawFee);
  if (!Number.isFinite(fee)) errors.feePercent = 'Enter a number';
  else if (fee < 0) errors.feePercent = 'Cannot be negative — use 0 for no fee';
  else if (fee > ctx.maxFeePercent) errors.feePercent = `Cannot exceed ${ctx.maxFeePercent}%`;
  else if (Math.round(fee * 100) !== fee * 100) errors.feePercent = 'At most 2 decimal places';

  const basis = str(form.metadata.feeBasis);
  if (!errors.feePercent && fee > 0) {
    if (!basis || basis === 'none') errors.feeBasis = 'Say what the fee covers — platform, gateway or both';
    else if (gatewayDef?.kind === 'offline' && basis !== 'platform') errors.feeBasis = `${gatewayDef.label} has no gateway charge — use Platform fee`;
  }
  return errors;
}
